import React, { useState, useRef } from 'react';
import './ResetPassword.css';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Lock, CheckCircle } from 'lucide-react';
import LogoImage from '../../../assets/Admin/AdminNavbar/logo-image.png';
import { useLocation, useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMesssage] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const passwordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);

  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token');

  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  const handleUserPasswordChange = e => {
    setPassword(e.target.value);
    setPasswordError(null);
  };
  const handleUserConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value);
    setPasswordError(null);
  };
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const password_validate = password => {
    const re = {
      capital: /(?=.*[A-Z])/,
      length: /.{8,40}/,
      specialChar: /[ -/:-@[-`{-~]/,
      digit: /(?=.*[0-9])/,
    };

    if (!re.length.test(password)) {
      return 'Password must be at least 8 characters long.';
    }
    if (!re.capital.test(password)) {
      return 'Password must include at least one uppercase letter.';
    }
    if (!re.specialChar.test(password)) {
      return 'Password must include at least one special character.';
    }
    if (!re.digit.test(password)) {
      return 'Password must include at least one digit.';
    }

    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const passwordValidationError = password_validate(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      toast.dismiss();
      toast.error('Passwords do not match.');
      return;
    }

    try {
      const newData = {
        token: token,
        password: password,
      };

      const res = await axios.post(`${SERVER_API_URL}/auth/reset-password`, newData);
      if (res?.status === 200) {
        setSuccessMesssage(true);
        setPasswordError(null);
      }
    } catch (error) {
      console.log(error);
      toast.dismiss();
      toast.error(error.response.data.detail);
    }
  };

  // Check if form is valid
  const isFormValid = password.trim() !== '' && confirmPassword.trim() !== '';

  return (
    <Box className="reset-password-container">
      {/* Header Section */}
      <Box className="reset-password-header">
        <Box className="reset-password-logo">
          <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
        </Box>
      </Box>

      {/* Main Content - Card */}
      {successMessage === false ? (
        <Box className="reset-password-card">
          <Box className="card-header">
            <Lock className="card-icon email-password-icon" />
            <Typography component="h3" className="card-title">
              Reset Password
            </Typography>
          </Box>

          <Typography variant="body2" className="card-description">
            Enter your new password below
          </Typography>

          <form className="card-form" onSubmit={e => handleSubmit(e)}>
            <Box className="input-group">
              <label htmlFor="password" className="input-label">
                New Password
              </label>
              <Box className="input-container">
                <Lock className="input-icon" />
                <input
                  id="password"
                  className="input-field"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={handleUserPasswordChange}
                  ref={passwordInputRef}
                  required
                  autoComplete="new-password"
                />
                <div
                  onClick={handleClickShowPassword}
                  className="password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? (
                    <VisibilityOff className="toggle-icon" />
                  ) : (
                    <Visibility className="toggle-icon" />
                  )}
                </div>
              </Box>
              {passwordError && <div className="error-message">{passwordError}</div>}
            </Box>

            <Box className="input-group">
              <label htmlFor="confirmPassword" className="input-label">
                Confirm Password
              </label>
              <Box className="input-container">
                <Lock className="input-icon" />
                <input
                  id="confirmPassword"
                  className="input-field"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={handleUserConfirmPasswordChange}
                  ref={confirmPasswordInputRef}
                  required
                  autoComplete="new-password"
                />
                <div
                  onClick={handleClickShowConfirmPassword}
                  className="password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff className="toggle-icon" />
                  ) : (
                    <Visibility className="toggle-icon" />
                  )}
                </div>
              </Box>
              {password !== confirmPassword && confirmPassword !== '' && (
                <div className="error-message">Passwords do not match.</div>
              )}
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isFormValid ? 'disabled' : ''}`}
              disabled={!isFormValid}
              data-testid="submit"
            >
              Confirm Password
            </button>
          </form>
        </Box>
      ) : (
        <Box className="reset-password-card">
          <Box className="card-header">
            <Box className="success-icon-container">
              <CheckCircle className="card-icon success-icon" />
            </Box>
            <Typography component="h3" className="card-title">
              Password Reset Successful
            </Typography>
          </Box>

          <Typography variant="body2" className="card-description">
            Your password has been changed successfully. You can now log in with your new password.
          </Typography>

          <button
            onClick={() => navigate('/')}
            className="primary-button"
            style={{ marginTop: '8px' }}
          >
            Sign In
          </button>
        </Box>
      )}
    </Box>
  );
};

export default ResetPassword;
